import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Animate } from 'react-move';
import { throttle } from 'lodash';
import { renderChildren } from '@fiverr-private/ui_utils';
import { useElementSize } from '@fiverr-private/hooks';
import type { CollapseProps, TransitionProps } from '../../types';
import { useTransitionEvents } from '../../hooks';
import variants from './variants';
import { calculateHeight } from './utils';
import { UPDATE_DYNAMIC_CONTENT_TIMEOUT } from './constants';

const Collapse = ({
  unmountOnExit,
  animateOpacity = true,
  startingHeight = 0,
  endingHeight = 'auto',
  delay,
  duration = 'moderate1',
  easing = 'easeInOut',
  in: isOpen = true,
  onAnimationStart,
  onAnimationEnd,
  children,
}: TransitionProps & CollapseProps) => {
  const isDynamicHeight = endingHeight === 'auto';
  const innerRef = useRef<HTMLDivElement>(null);
  const ref = children?.ref || innerRef;

  const [elementHeight, setElementHeight] = useState(isDynamicHeight ? 0 : endingHeight);

  const { getEventsHandler } = useTransitionEvents({
    onAnimationStart,
    onAnimationEnd,
    isOpen,
    unmountOnExit,
  });

  const getEndingHeight = () => {
    if (!isDynamicHeight) {
      return endingHeight;
    }

    if (elementHeight) {
      return elementHeight;
    }

    return ref.current?.scrollHeight || 0;
  };

  const custom = {
    startingHeight,
    endingHeight: getEndingHeight(),
    animateOpacity,
    unmountOnExit,
    duration,
    easing,
    delay,
  };

  const handleMutationObserverChange = useCallback(
    (entries) => {
      if (!ref.current) {
        return;
      }

      entries.forEach(() => {
        const newHeight = calculateHeight(ref);
        setElementHeight(newHeight);
      });
    },
    [ref]
  );

  useEffect(() => {
    if (!ref.current || !isDynamicHeight) {
      return;
    }

    const handleMutationObserverThrottled = throttle(handleMutationObserverChange, UPDATE_DYNAMIC_CONTENT_TIMEOUT);
    const mutationObserver = new MutationObserver(handleMutationObserverThrottled);
    mutationObserver.observe(ref.current, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['style', 'class', 'height'],
    });

    setElementHeight(ref.current.scrollHeight);

    return () => mutationObserver.disconnect();
  }, [ref, isDynamicHeight, handleMutationObserverChange]);

  const startVariant = variants.start?.(custom) || variants.enter(custom);

  const handleWindowResizeThrottled = useMemo(
    () =>
      throttle(() => {
        if (!ref.current) {
          return;
        }

        const newHeight = calculateHeight(ref);
        setElementHeight(newHeight);
      }, UPDATE_DYNAMIC_CONTENT_TIMEOUT),
    [ref]
  );

  const { width: windowWidth, height: windowHeight } = useElementSize(typeof window !== 'undefined' ? window : null);
  useEffect(() => handleWindowResizeThrottled, [windowWidth, windowHeight, handleWindowResizeThrottled]);

  return (
    <Animate
      show={unmountOnExit ? isOpen : true}
      start={isOpen ? startVariant : variants.exit(custom)}
      enter={{
        ...(isOpen ? variants.enter(custom) : variants.exit(custom)),
        ...getEventsHandler('enter'),
      }}
      update={{
        ...(isOpen ? startVariant : variants.exit(custom)),
        ...getEventsHandler('update'),
      }}
      leave={{
        ...variants.exit(custom),
        ...getEventsHandler('leave'),
      }}
    >
      {({ height, opacity, timing, isFirstRender }) => {
        const heightStyle = isFirstRender && isDynamicHeight ? 'auto' : `${height}px`;

        return renderChildren(children, {
          ref,
          style: {
            ...(children.props.style || {}),
            overflow: 'hidden',
            height: heightStyle,
            opacity,
            timing,
          },
        });
      }}
    </Animate>
  );
};

Collapse.displayName = 'Collapse';

export default Collapse;
